import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import api from '../utils/api';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  useEffect(() => {
    // Get pending user info
    const storedEmail = sessionStorage.getItem('verificationEmail');
    const storedUser = sessionStorage.getItem('pendingUser');
    
    if (!storedEmail || !storedUser) {
      navigate('/register');
      return;
    }

    setEmail(storedEmail);
    setPendingUser(JSON.parse(storedUser));

    // Start countdown if there's a stored timestamp
    const lastSentTime = sessionStorage.getItem('emailOtpSentTime');
    if (lastSentTime) {
      const elapsed = Date.now() - parseInt(lastSentTime);
      const remaining = Math.max(0, 60 - Math.floor(elapsed / 1000));
      setCountdown(remaining);
    }
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const onSubmit = async (data) => {
    try {
      const response = await api.post('/verify-otp', {
        type: 'email',
        identifier: email,
        otp: data.otp
      });

      const result = response.data;
      
      // Check verification_status object if it exists
      if (result.verification_status) {
        const { email_verified, phone_verified } = result.verification_status;
        
        if (email_verified && phone_verified) {
          // Both verified - go to dashboard
          toast.success('Account fully verified! Redirecting to dashboard...');
          sessionStorage.removeItem('emailOtpSentTime');
          sessionStorage.removeItem('verificationEmail');
          sessionStorage.removeItem('verificationPhone');
          sessionStorage.removeItem('pendingUser');
          navigate('/dashboard');
        } else if (email_verified && !phone_verified) {
          // Email verified but phone not - go to phone verification
          toast.success('Email verified successfully!');
          sessionStorage.removeItem('emailOtpSentTime');
          navigate('/verify-mobile');
        } else {
          // Handle other cases
          toast.error('Verification status unclear. Please try again.');
        }
      } else if (result.message === 'Email verified successfully' || result.message === 'OTP verified successfully') {
        // Fallback to old behavior if verification_status is not present
        toast.success('Email verified successfully!');
        sessionStorage.removeItem('emailOtpSentTime');
        navigate('/verify-mobile');
      } else {
        toast.error(result.message || 'Invalid OTP');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Verification failed. Please try again.');
      }
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;
    
    setIsResending(true);
    try {
      const response = await api.post('/resend-otp', { 
        type: 'email',
        identifier: email
      });

      const result = response.data;
      
      if (result.message && result.message.includes('sent')) {
        toast.success('Email OTP resent successfully!');
        setCountdown(60);
        sessionStorage.setItem('emailOtpSentTime', Date.now().toString());
      } else {
        toast.error(result.message || 'Failed to resend OTP');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to resend OTP. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl p-8">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4"
            >
              <Mail className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
            <p className="text-sm text-gray-600">We've sent a verification code to</p>
            <p className="text-primary-600 font-medium">{email}</p>
          </div>

          {/* User Info */}
          {pendingUser && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {pendingUser.first_name[0]}{pendingUser.last_name[0]}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {pendingUser.first_name} {pendingUser.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{pendingUser.email}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* OTP Field */}
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter Email Verification Code
              </label>
              <input
                id="otp"
                type="text"
                className={`input-field text-center text-2xl tracking-widest ${errors.otp ? 'border-red-500' : ''}`}
                placeholder="000000"
                maxLength="6"
                {...register('otp', {
                  required: 'OTP is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'OTP must be 6 digits',
                  },
                })}
              />
              {errors.otp && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-1 text-sm text-red-600"
                >
                  {errors.otp.message}
                </motion.p>
              )}
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify Email'
              )}
            </motion.button>
          </form>

          {/* Resend OTP */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-3">
              Didn't receive the code?
            </p>
            <button
              onClick={resendOTP}
              disabled={countdown > 0 || isResending}
              className="font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto space-x-2"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Resending...</span>
                </>
              ) : countdown > 0 ? (
                <span>Resend OTP in {countdown}s</span>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend OTP</span>
                </>
              )}
            </button>
          </div>

          {/* Back to Register Link */}
          <div className="mt-4">
            <button
              onClick={() => navigate('/register')}
              className="flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-primary-600 transition-colors mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to register</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;